import { useRef, useState } from "react";
import { Zap } from "lucide-react";
import { Headlights } from "./Headlights";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { MaskLines, Eyebrow, EASE } from "../site/Motion";
import { Swoosh } from "../site/Swoosh";
import { SearchCard } from "../site/SearchCard";

const BusStage = () => {
  const ref = useRef(null);
  const [lights, setLights] = useState(false);
  const ptr = useRef("mouse");
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} onMouseMove={move} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative [perspective:1400px]">
      <Swoosh className="absolute -left-[4%] -top-[10%] h-[120%] w-[112%]" />
      <motion.div style={{ rotateX: rx, rotateY: ry, y }} className="relative [transform-style:preserve-3d]">
        <motion.div
          initial={{ clipPath: "inset(0 0 0 100%)" }}
          animate={{ clipPath: "inset(0 0 0 0%)" }}
          transition={{ duration: 1.4, ease: EASE, delay: 0.35 }}
        >
          <div
            data-testid="hero-bus-lights-toggle"
            onPointerDown={(e) => { ptr.current = e.pointerType; }}
            onPointerEnter={(e) => e.pointerType === "mouse" && setLights(true)}
            onPointerLeave={(e) => e.pointerType === "mouse" && setLights(false)}
            onClick={() => ptr.current !== "mouse" && setLights((v) => !v)}
            className="chamfer relative aspect-[3/2] cursor-pointer overflow-hidden bg-[var(--ink)] shadow-2xl"
          >
            <motion.img
              src="/brand/bus.jpg"
              alt="Ómnibus doble piso Santiagueño"
              data-testid="hero-bus-image"
              className="h-full w-full object-cover"
              initial={{ scale: 1.25 }}
              animate={{ scale: 1 }}
              transition={{ duration: 2, ease: EASE, delay: 0.35 }}
            />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_70%_40%,transparent_40%,rgba(11,17,13,.45))]" />
            <Headlights on={lights} />
            <span className={`pointer-events-none absolute right-[9%] top-4 flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[11px] tracking-widest backdrop-blur-md transition-colors duration-300 ${lights ? "bg-[var(--lime)] text-[var(--ink)]" : "bg-black/55 text-white"}`}>
              <Zap className="h-3.5 w-3.5" /> <span className="md:hidden">TOCÁ PARA LAS LUCES</span><span className="hidden md:inline">PASÁ EL MOUSE: LUCES</span>
            </span>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8, ease: EASE }} style={{ transform: "translateZ(60px)" }} className="absolute -bottom-5 left-4 flex items-center gap-3 rounded-full bg-[var(--ink)] py-2.5 pl-3 pr-5 text-white shadow-xl md:left-10">
          <span className="h-3 w-3 rounded-full bg-[var(--lime)] pulse-dot" />
          <span className="font-mono text-xs tracking-widest">DOBLE PISO · STARLINK A BORDO</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden bg-[var(--paper)]">
    <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 pb-10 pt-8 md:px-10 md:pt-14 lg:grid-cols-12 lg:gap-6 lg:pb-16">
      <div className="relative z-10 lg:col-span-5">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}><Eyebrow>Conectamos historias</Eyebrow></motion.div>
        <h1 data-testid="hero-title" className="mt-6 font-display text-[2.9rem] sm:text-6xl lg:text-6xl xl:text-7xl">
          <MaskLines lines={["Tu próximo", "viaje empieza", <span key="a" className="text-[var(--forest)]">acá<span className="text-[var(--lime)] [-webkit-text-stroke:2px_var(--forest)]">.</span></span>]} delay={0.15} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8, ease: EASE }} className="mt-7 max-w-md text-base text-[var(--muted-ink)] md:text-lg">
          Buenos Aires, Santiago del Estero y Tucumán, más cerca. Viajá en doble piso con Wi‑Fi satelital y butaca a elección.
        </motion.p>
      </div>
      <div className="relative lg:col-span-7"><BusStage /></div>
    </div>
    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: EASE }} className="relative z-20 mx-auto max-w-[1400px] px-5 pb-16 md:px-10 md:pb-24">
      <SearchCard />
    </motion.div>
  </section>
);
